/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtData(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function DocumentiAlunnoModal({ alunno, close }) {
  // alunno: { Cod, Cog, Nom, classe, settore }
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingCat, setUploadingCat] = useState(null);
  const fileInputs = useRef({});

  const cod = alunno?.Cod || alunno?.cod;

  const load = async () => {
    if (!cod) return;
    setLoading(true);
    try {
      const r = await api.get(`/documenti-alunni/alunno/${cod}/lista`);
      setItems(r.data.items || []);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [cod]);

  const onPickFile = (catId) => {
    const el = fileInputs.current[catId];
    if (el) el.click();
  };

  const onFileChange = async (catId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCat(catId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documenti-alunni/alunno/${cod}/categoria/${catId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Errore upload");
    } finally {
      setUploadingCat(null);
      e.target.value = "";
    }
  };

  // URL diretto al file: la cookie 'access_token' viene inviata automaticamente
  // (axios usa withCredentials e i CORS lo permettono). Questo è più affidabile
  // del fetch+blob+window.open perché:
  // 1) Bypassa i popup-blocker (handler sincrono al click)
  // 2) Il browser rispetta Content-Disposition (inline → preview PDF; attachment → download)
  // 3) Per file non visualizzabili (docx, xlsx) con inline il browser scarica autonomamente
  const fileUrl = (catId, inline) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/documenti-alunni/alunno/${cod}/categoria/${catId}/file?inline=${inline ? "true" : "false"}`;
  };

  const view = (catId) => {
    window.open(fileUrl(catId, true), "_blank", "noopener,noreferrer");
  };

  const download = (catId) => {
    // Click sintetico su <a> con il param attachment → browser triggera il download
    const a = document.createElement("a");
    a.href = fileUrl(catId, false);
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const remove = async (catId, descrizione) => {
    if (!window.confirm(`Eliminare il documento "${descrizione}"?`)) return;
    try {
      await api.delete(`/documenti-alunni/alunno/${cod}/categoria/${catId}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div data-testid="docs-alunno-modal" style={{ background: "white", borderRadius: 6, width: 900, maxWidth: "94vw", maxHeight: "90vh", overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
        <div style={{ padding: "12px 16px", background: "#1565c0", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            📁 Documenti — <b>{alunno?.Cog || alunno?.cognome} {alunno?.Nom || alunno?.nome}</b> (cod. {cod})
          </div>
          <button onClick={close} style={{ background: "transparent", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ padding: 30, textAlign: "center", color: "#666" }}>Caricamento…</div> : (
            items.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "#888", background: "#f5f5f5", borderRadius: 4 }}>
                Nessuna categoria di documenti configurata.<br />
                Vai in <b>Anagrafica &gt; 📂 Catalogo Documenti</b> per crearle.
              </div>
            ) : (
              <table className="sw-data-table" style={{ width: "100%" }}>
                <thead>
                  <tr style={{ background: "#37474f", color: "white" }}>
                    <th style={{ padding: 8, textAlign: "left" }}>Categoria</th>
                    <th style={{ padding: 8, textAlign: "left" }}>Documento</th>
                    <th style={{ padding: 8, textAlign: "center", width: 130 }}>Dimensione</th>
                    <th style={{ padding: 8, textAlign: "center", width: 150 }}>Caricato il</th>
                    <th style={{ padding: 8, textAlign: "center", width: 220 }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const d = it.documento;
                    const uploading = uploadingCat === it.categoria_id;
                    return (
                      <tr key={it.categoria_id} data-testid={`docs-row-${it.categoria_codice}`}>
                        <td style={{ padding: 8, fontWeight: 600 }}>
                          {it.categoria_descrizione}
                          <div style={{ fontSize: 9, color: "#888", fontFamily: "monospace" }}>{it.categoria_codice}</div>
                        </td>
                        <td style={{ padding: 8, fontSize: 11 }}>
                          {d ? (
                            <>
                              <div style={{ fontWeight: 600, color: "#1565c0" }}>{d.filename}</div>
                              <div style={{ fontSize: 9, color: "#888" }}>
                                {d.content_type}
                                {d.uploaded_by && <> · {d.uploaded_by} ({d.uploaded_role})</>}
                              </div>
                            </>
                          ) : (
                            <span style={{ color: "#bbb", fontStyle: "italic" }}>Nessun documento</span>
                          )}
                        </td>
                        <td style={{ padding: 8, textAlign: "center", fontSize: 11, color: "#555" }}>{fmtBytes(d?.size)}</td>
                        <td style={{ padding: 8, textAlign: "center", fontSize: 10, color: "#666", fontFamily: "monospace" }}>{fmtData(d?.uploaded_at)}</td>
                        <td style={{ padding: 8, textAlign: "center", whiteSpace: "nowrap" }}>
                          <input type="file" ref={el => fileInputs.current[it.categoria_id] = el}
                            onChange={(e) => onFileChange(it.categoria_id, e)} style={{ display: "none" }} />
                          <button onClick={() => onPickFile(it.categoria_id)} disabled={uploading}
                            data-testid={`docs-upload-${it.categoria_codice}`}
                            title={d ? "Sostituisci documento" : "Carica documento"}
                            style={{ padding: "4px 8px", background: d ? "#ef6c00" : "#2e7d32", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: uploading ? "wait" : "pointer", borderRadius: 2, marginRight: 4 }}>
                            {uploading ? "⏳" : (d ? "↻ Sostituisci" : "↑ Carica")}
                          </button>
                          {d && (
                            <>
                              <button onClick={() => view(it.categoria_id)} title="Visualizza"
                                data-testid={`docs-view-${it.categoria_codice}`}
                                style={{ padding: "4px 8px", background: "#1565c0", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2, marginRight: 4 }}>
                                👁
                              </button>
                              <button onClick={() => download(it.categoria_id)} title="Scarica"
                                data-testid={`docs-down-${it.categoria_codice}`}
                                style={{ padding: "4px 8px", background: "#37474f", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2, marginRight: 4 }}>
                                ⤓
                              </button>
                              <button onClick={() => remove(it.categoria_id, it.categoria_descrizione)} title="Elimina"
                                data-testid={`docs-del-${it.categoria_codice}`}
                                style={{ padding: "4px 8px", background: "#b71c1c", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                                🗑
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
    </div>
  );
}
